<template>
  <div class="w-full flex flex-col gap-6">
    <Form
      v-if="!isLoading"
      :initial-values="searchCriteria"
      :keep-values="true"
      class="w-full flex flex-col gap-3"
      v-slot="{ resetField, values }"
      @submit="(values) => onSubmit(values)"
    >
      <fieldset class="border-2 border-primary rounded p-4 flex flex-col gap-y-4">
        <legend class="px-4 text-lg md:text-xl">{{ $t('Search') }}</legend>
        <div class="flex flex-col gap-2">
          <div class="w-full flex gap-2">
            <Field
              :rules="requiredRule"
              name="dateFrom"
              v-slot="{ field, errors: errs, handleChange }"
            >
              <DatepickerV2
                :key="values.dateTo ?? new Date()"
                :field="field"
                :errors="errs"
                :label="$t('fromDate')"
                :default-value="field.value"
                :server-date-format="DATE_FORMAT"
                :max-date="values.dateTo ?? '__now__'"
                input-class="ps-3 py-0.5"
                class="w-1/2"
                @on-change="
                  (date) => {
                    handleChange(date)
                    resetField('endDate')
                  }
                "
              >
              </DatepickerV2>
            </Field>
            <Field
              :rules="requiredRule"
              name="dateTo"
              v-slot="{ field, errors: errs, handleChange }"
            >
              <DatepickerV2
                :key="values.dateFrom"
                :field="field"
                :errors="errs"
                :label="$t('toDate')"
                :default-value="field.value"
                :server-date-format="DATE_FORMAT"
                :min-date="values.dateFrom"
                max-date="__now__"
                input-class="ps-3 py-0.5"
                class="w-1/2"
                @on-change="(date) => handleChange(date)"
              >
              </DatepickerV2>
            </Field>
          </div>
          <div class="w-full flex flex-col md:flex-row gap-2">
            <Field name="lookupSessionStatus" v-slot="{ field, errors: errs, handleChange }">
              <Dropdown
                :field="field"
                :errors="errs"
                :label="$t('sessionStatus')"
                :options="sessionStatuses"
                :is-required="false"
                class="w-full md:w-1/2"
                direction="flex-col"
                @on-selection="(item) => handleChange(+item?.code)"
              ></Dropdown>
            </Field>
            <Field name="lookupLoginTypeId" v-slot="{ field, errors: errs, handleChange }">
              <Dropdown
                :field="field"
                :errors="errs"
                :label="$t('loginType')"
                :options="loginTypes"
                :is-required="false"
                class="w-full md:w-1/2"
                direction="flex-col"
                @on-selection="(item) => handleChange(+item?.code)"
              ></Dropdown>
            </Field>
          </div>
        </div>
        <div class="w-full flex justify-end">
          <button
            class="min-w-28 text-center rounded-md bg-primary/80 pe-2 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
          >
            <div class="p-2 bg-primary rounded-s-md">
              <faMagnifyingGlass class="w-4 fill-white"></faMagnifyingGlass>
            </div>
            <span class="w-full text-center">
              {{ $t('Search') }}
            </span>
          </button>
        </div>
      </fieldset>
    </Form>
    <template v-if="!isLoading">
      <DataTable
        :default-headers="[
          'loginType',
          'startSessionDate',
          'endSessionDate',
          'loginDevice',
          'loginPlatform',
          'loginLocation',
          'sessionStatus'
        ]"
        :data-set="dataSet"
      ></DataTable>
      <div
        v-if="dataSet.length > 5"
        class="py-2 w-full flex flex-col md:flex-row gap-y-4 items-center justify-center md:justify-between"
      >
        <div class="w-56 flex border border-brand-gray-300 rounded-md">
          <button
            :key="pc"
            v-for="pc in pageCounts"
            :class="{ 'bg-primary text-white': paginatorConfig.itemsPerPage == pc }"
            class="flex-1 p-1 border-e first:rounded-s-md last:rounded-e-md last:border-none border-brand-gray-300 hover:bg-primary/70"
            @click="onPageCountChange(pc)"
          >
            {{ pc }}
          </button>
        </div>
        <Paginate
          :page-count="paginatorConfig.pageCount"
          :page-range="paginatorConfig.itemsPerPage"
          :click-handler="onPageClick"
          :prev-text="'‹'"
          :next-text="'›'"
          :first-last-button="true"
          v-model="paginatorConfig.currentPage"
          container-class="pagination"
          first-button-text="«"
          last-button-text="»"
          class="accent-primary-700"
        />
        <div class="w-56 flex justify-center md:justify-end items-center gap-x-2">
          <input
            type="text"
            class="w-full max-w-[4ch] py-1.5 rounded-md border border-brand-gray-600 outline-none text-center focus:border-primary"
            v-model.number="paginatorConfig.currentPage"
            @input="onOverridePage(paginatorConfig.currentPage)"
          />
          <span class="">{{ $t('of') }} {{ paginatorConfig.pageCount }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import faMagnifyingGlass from '@/assets/icons/faMagnifyingGlass.svg?component'
import DataTable from '@/components/DataTable/index.vue'
import DatepickerV2 from '@/components/SimpleForm/DatepickerV2.vue'
import Dropdown from '@/components/SimpleForm/Dropdown.vue'
import axiosInstance from '@/services'
import { useLookupsStore } from '@/stores/lookups'
import { DateTime } from 'luxon'
import { Field, Form } from 'vee-validate'
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Paginate from 'vuejs-paginate-next'

function requiredRule(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return true
}

const DATE_FORMAT = 'yyyy-MM-dd'

const lookupsStore = useLookupsStore()

// reactive variable to hold api response
const rawTable = ref()
const loginTypes = ref()
const sessionStatuses = ref()
const i18n = useI18n()
const isLoading = ref(true)

const pageCounts = [5, 10, 20, 50, 100]

const searchCriteria = reactive({
  dateFrom: null,
  dateTo: null,
  lookupSessionStatus: null,
  lookupLoginTypeId: null
})

const paginatorConfig = reactive({
  totalItemCount: 0,
  itemsPerPage: 10,
  currentPage: 1,
  pageCount: 0
})

const dataSet = computed(() => {
  return rawTable.value.map((r) => {
    const {
      lookupLoginTypeId,
      dateFrom,
      dateTo,
      loginDevice,
      loginPlatform,
      loginLocation,
      lookupSessionStatus
    } = r

    const loginType =
      loginTypes.value.find((lt) => lt.id == lookupLoginTypeId)?.localizedName ?? '--'

    const sessionStatus =
      sessionStatuses.value.find((s) => s.id == lookupSessionStatus)?.localizedName ?? '--'

    const startSessionDate = dateFrom
      ? DateTime.fromISO(dateFrom).toFormat('dd/MM/yyyy HH:mm:ss a')
      : '--'

    const endSessionDate = dateTo
      ? DateTime.fromISO(dateTo).toFormat('dd/MM/yyyy HH:mm:ss a')
      : '--'

    return {
      loginType,
      startSessionDate,
      endSessionDate,
      loginDevice,
      loginPlatform,
      loginLocation,
      sessionStatus
    }
  })
})

onMounted(async () => {
  searchCriteria.dateFrom = DateTime.now().minus({ days: 7 }).toFormat(DATE_FORMAT)
  searchCriteria.dateTo = DateTime.now().plus({ days: 1 }).toFormat(DATE_FORMAT)
  loginTypes.value = await lookupsStore.fetchLookup('/api/lookup/getLoginTypes')
  sessionStatuses.value = await lookupsStore.fetchLookup('/api/lookup/getSessionStatus')
  await fetchTable()
})

const fetchTable = async (form = {}) => {
  const body = {
    currentPage: paginatorConfig.currentPage,
    pageSize: paginatorConfig.itemsPerPage,
    data: {
      dateFrom: searchCriteria.dateFrom,
      dateTo: searchCriteria.dateTo,
      lookupSessionStatus: form?.lookupSessionStatus ?? searchCriteria.lookupSessionStatus,
      lookupLoginTypeId: form?.lookupLoginTypeId ?? searchCriteria.lookupLoginTypeId
    }
  }

  const response = await axiosInstance.post('/api/user/loggedInHistory/search', body)

  const { collection, numberOfRecords } = response

  rawTable.value = collection
  paginatorConfig.totalItemCount = numberOfRecords
  paginatorConfig.pageCount = Math.ceil((numberOfRecords || 1) / paginatorConfig.itemsPerPage)

  isLoading.value = false
}

const onSubmit = async (form) => {
  searchCriteria.dateFrom = form.dateFrom
  searchCriteria.dateTo = form.dateTo
  await fetchTable(form)
}

const onPageClick = async (page) => {
  paginatorConfig.currentPage = page
  await fetchTable()
}

const onPageCountChange = async (itemsPerPage) => {
  paginatorConfig.currentPage = 1
  paginatorConfig.itemsPerPage = itemsPerPage
  await fetchTable()
}

const onOverridePage = async (currentPage) => {
  if (currentPage <= 0) {
    paginatorConfig.currentPage = 1
  } else if (currentPage > paginatorConfig.pageCount) {
    paginatorConfig.currentPage = paginatorConfig.pageCount
  } else paginatorConfig.currentPage = currentPage

  await fetchTable()
  return
}
</script>

<style lang="scss" scoped></style>
